import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { any, append } from 'ramda';
import { formatPhoneNumber } from '../../../../common/helpers/phoneHelper';
import { IRefReqSelectedContact } from '../../../../common/models/contact/IRefReqSelectedContact';
import { ContactType } from '../../../../common/enums/ContactType';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import { ReactComponent as UserIcon } from '../../../../assets/icons/user.svg';
import { ReactComponent as Warning } from '../../../../assets/icons/warning.svg';
import { ILastRole } from '../../../../common/models/candidate/ILastRole';
import { Column } from '../../../../common/models/table/Column';
import Tooltip from '../../../../components/Tooltip';
import { IBindingCallback1, IBindingCallback2 } from '../../../../common/models/callback';
import styles from './styles.module.scss';
import RegularButton from '../../../../components/RegularButton';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import TextOverflowTooltip from '../../../../components/TextOverflowTooltip';
import { INewContactInfo } from '../../../../common/models/contact/INewContactInfo';

export const unreachablePhone = {
  id: 'unreachablePhone',
  message: 'This number cannot receive text messages. Please provide a mobile number or use email address instead.'
};

export interface IEditContactPayload {
  accessor: keyof INewContactInfo;
  index: number;
  value: string;
}

export const isWarningShown = (candidates: IRefReqSelectedContact[]) => (
  any((cand: IRefReqSelectedContact) => Boolean(cand.activeCandidate))(candidates)
);

const notProvided = (
  <span className="secondary-text">
    <FormattedMessage id="notProvided" defaultMessage="not provided" />
  </span>
);

export const getSelectedContactsColumns = (
  { withWarning, position, actions }: {
    withWarning: boolean,
    position?: string,
    actions?: {
      onDelete: IBindingCallback1<INewContactInfo>;
      onEdit: IBindingCallback2<number, INewContactInfo>;
    }
  }
): Column[] => {
  const columns: Column[] = [
    {
      accessor: 'icon',
      Cell: () => <div className="table-main-icon"><UserIcon /></div>,
      editable: false,
      width: 50
    },
    {
      Header: () => <FormattedMessage id="email" defaultMessage="Email" />,
      accessor: 'email',
      Cell: ({ cell: { value } }) => (value
        ? (
          <TextOverflowTooltip content={value} width={204}>
            {value}
          </TextOverflowTooltip>
        )
        : notProvided
      ),
      width: 170,
      inputType: 'email'
    },
    {
      Header: () => <FormattedMessage id="firstName" defaultMessage="First name" />,
      accessor: 'firstName',
      Cell: ({ cell: { value } }) => <TextOverflowTooltip content={value} width={148}>{value}</TextOverflowTooltip>,
      width: 120
    },
    {
      Header: () => <FormattedMessage id="lastName" defaultMessage="Last name" />,
      accessor: 'lastName',
      Cell: ({ cell: { value } }) => <TextOverflowTooltip content={value} width={148}>{value}</TextOverflowTooltip>,
      width: 120
    },
    {
      Header: () => <FormattedMessage id="phoneNumber" defaultMessage="Phone number" />,
      accessor: 'phoneNumber',
      Cell: ({ cell: { value } }) => (value ? formatPhoneNumber(value) : notProvided),
      width: 170,
      inputType: 'phoneNumber'
    },
    {
      Header: () => (withWarning ? <FormattedMessage id="alerts" defaultMessage="Alerts" /> : null),
      id: 'alert',
      Cell: ({ cell: { row: { original } } }) => {
        const { activeCandidate } = original as IRefReqSelectedContact;
        const tooltipContent = activeCandidate && (
          <ul className="list">
            {position !== activeCandidate.position && (
              <li>
                <FormattedMessage id="active" defaultMessage="Active" />
                &nbsp;
                <FormattedMessage
                  id="step2.candidate"
                  defaultMessage="Candidate for {position}"
                  values={{ position: (
                    <strong>
                      &nbsp;
                      {activeCandidate.position}
                    </strong>
                  ) }}
                />
              </li>
            )}
            <li className={styles.listOpt}>
              <strong>
                {activeCandidate.refCount}
                &nbsp;
                <FormattedMessage
                  id={activeCandidate.refCount === 1 ? 'reference' : 'card.references.title'}
                  defaultMessage="reference(s)"
                />
              </strong>
              &nbsp;
              <FormattedMessage id="step2.tooltip.alreadyReq" defaultMessage="already requested" />
            </li>
          </ul>
        );

        return activeCandidate
          ? (
            <div className="table-center" style={{ maxWidth: '40px' }}>
              <Tooltip content={tooltipContent as JSX.Element}>
                <div className={`${styles.warningColumn} cursor-pointer`}>
                  <Warning />
                </div>
              </Tooltip>
            </div>
          )
          : null;
      },
      width: 70,
      editable: false
    }
  ];

  const actionsColumn: Column = {
    Header: () => <FormattedMessage id="actions" defaultMessage="Actions" />,
    id: 'actions',
    Cell: api => {
      const rowEditData = api.getRowEditData();
      return (rowEditData?.index === api.row.index
        ? (
          <div className="bttns-container">
            <RegularButton
              size="sm"
              variant="tertiary"
              textIntlId="cancel"
              defaultMessage="Cancel"
              onClick={() => api.cancelRowEdit()}
            />
            <RegularButton
              size="sm"
              textIntlId="save"
              defaultMessage="Save"
              onClick={() => rowEditData && actions?.onEdit(rowEditData.index, {
                firstName: rowEditData.values.firstName.trim(),
                lastName: rowEditData.values.lastName.trim(),
                email: rowEditData.values.email.trim() || null,
                phoneNumber: rowEditData.values.phoneNumber?.trim() || null
              })}
            />
          </div>
        )
        : (
          <div style={{ maxWidth: '50px' }} className="table-actions table-center">
            <IconButton
              tooltip={{ id: 'candidateActions.edit', defaultMessage: 'Edit name, email, phone' }}
              icon={<Icon icon="pencil" />}
              onClick={() => api.editRow(api.row)}
              variant="blue"
            />
            <IconButton
              onClick={() => actions?.onDelete(api.row.original as INewContactInfo)}
              icon={<Icon icon="minusCircle" />}
              tooltip={{ id: 'remove', defaultMessage: 'Remove' }}
              variant="blue"
            />
          </div>
        ));
    },
    editable: false,
    width: 120
  };

  return actions ? append(actionsColumn, columns) : columns;
};

export const getHint = (lastRole: ILastRole) => lastRole && (
  lastRole.role === ContactType.Candidate
    ? (
      <>
        {lastRole.status === CandidateStatus.Active
          ? <FormattedMessage id="active" defaultMessage="Active" />
          : <FormattedMessage id="prior" defaultMessage="Prior" />}
        &nbsp;
        <FormattedMessage
          id="step2.candidate"
          defaultMessage="сandidate for {position}"
          values={{ position: lastRole.position }}
        />
        {lastRole.status !== CandidateStatus.Active && ` ${moment(lastRole.date).format('MM/YYYY')}`}
      </>
    )
    : (
      <>
        <FormattedMessage id="prior" defaultMessage="Prior" />
        &nbsp;
        <FormattedMessage
          id="step2.lastRole"
          defaultMessage="Reference for {position} {date}"
          values={{
            ...lastRole,
            date: moment(lastRole.date).format('MM/YYYY')
          }}
        />
      </>
    )
);

export const emailChangedModalText = {
  titleText: {
    id: 'step2.confirmChangeEmail.title',
    defaultMessage: 'Change {firstName}’s email address?'
  },
  descriptionText: {
    id: 'step2.confirmChangeEmail.description',
    // eslint-disable-next-line max-len
    defaultMessage: 'Just confirming you want to update {firstName} {lastName}’s profile with this new email address?{indent}If you meant to create a {newLabel} candidate with this email address, go back and remove {firstName} and add your new candidate with the “+ Add Candidate” button.'
  }
};

export const phoneChangedModalText = {
  titleText: {
    id: 'step2.confirmChangePhone.title',
    defaultMessage: 'Change {firstName}’s phone number?'
  },
  descriptionText: {
    id: 'step2.confirmChangePhone.description',
    // eslint-disable-next-line max-len
    defaultMessage: 'Just confirming you want to update {firstName} {lastName}’s profile with this new phone number?{indent}If you meant to create a {newLabel} candidate with this phone number, go back and remove {firstName} and add your new candidate with the “+ Add Candidate” button.'
  }
};

export const bothChangedModalText = {
  titleText: {
    id: 'step2.confirmChange.title',
    defaultMessage: 'Change {firstName}’s email address and phone number?'
  },
  descriptionText: {
    id: 'step2.confirmChange.description',
    // eslint-disable-next-line max-len
    defaultMessage: 'Just confirming you want to update {firstName} {lastName}’s profile with this new email address and phone number?{indent}If you meant to create a {newLabel} candidate with this new email address and phone number, go back and remove {firstName} and add your new candidate with the “+ Add Candidate” button.'
  }
};
